import {
  InfiniteData,
  keepPreviousData,
  useInfiniteQuery,
  UseInfiniteQueryOptions,
  useMutation,
  UseMutationOptions,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from '@tanstack/react-query';
import {
  api,
  ContactsListDto,
  CopyVendorAccountDto,
  CreateVendorAccountDto,
  FindVendorAccountDuplicatesDto,
  QueryKey,
  SelectListDto,
  ServiceCategoryDto,
  SpendCategoryListDto,
  CreateVendorDto,
  FileUploadDto,
  UpdateVendorAccountDto,
  UpdateVendorBillPayDto,
  UpdateVendorDto,
  VectorContractsListDto,
  VendorAccountDto,
  VendorAccountDuplicatesDto,
  VendorAccountListDto,
  VendorContractListDto,
  VendorCreateManyDto,
  VendorDetailDto,
  VendorListDto,
  VendorSelectListDto,
  VendorsListDto,
  VendorImportResultDto,
  VendorOrganizationSelectListDto,
  VendorClientListDto,
} from '@sib/api';
import { DEFAULT_PAGE } from '@sib/api/constants';
import {
  mapSelectListToOptions,
  type SelectOption,
} from '../utils/select-list';
import { invalidateAuditVendorAccountQueries } from './vendor-accounts';

const CACHE_TIME = 5000;

type SelectListOptionsQuery = Partial<
  UseQueryOptions<SelectListDto, Error, SelectOption[]>
>;

export const useVendorsList = (
  organizationId: string,
  query: Parameters<
    typeof api.organizations.organizationVendorsControllerGetAllVendorsByOrganizationName
  >[1],
  options?: UseQueryOptions<VendorsListDto>
) =>
  useQuery({
    ...options,
    enabled: !!organizationId,
    queryKey: [QueryKey.Resource.Vendors, query, organizationId],
    gcTime: CACHE_TIME,
    staleTime: CACHE_TIME,
    queryFn: async () =>
      api.organizations.organizationVendorsControllerGetAllVendorsByOrganizationName(
        organizationId,
        query
      ),
  });

export const useGetVendorsSelectInfiniteList = (
  query: Parameters<
    typeof api.vendors.vendorsControllerGetAllVendorsSelectList
  >[0],
  options?: Partial<
    UseInfiniteQueryOptions<
      VendorSelectListDto,
      Error,
      InfiniteData<VendorSelectListDto>
    >
  >
) =>
  useInfiniteQuery({
    ...options,
    queryKey: [
      QueryKey.Resource.VendorsSelectList,
      QueryKey.Operation.List,
      query,
      QueryKey.Operation.Infinite,
      ...(Array.isArray(options?.queryKey) ? options.queryKey : []),
    ],
    queryFn: async ({ pageParam }) =>
      api.vendors.vendorsControllerGetAllVendorsSelectList({
        ...query,
        page: pageParam as number,
      }),
    getNextPageParam: lastPage =>
      lastPage.hasNextPage ? lastPage.page + 1 : undefined,
    initialPageParam: query?.page || DEFAULT_PAGE,
    placeholderData: keepPreviousData,
  });

export const useInternalPortalVendorsList = (
  query: Parameters<typeof api.internalPortal.vendorsControllerGetVendors>[0],
  options?: UseQueryOptions<VendorListDto>
) =>
  useQuery({
    queryKey: [
      QueryKey.Resource.InternalPortalVendors,
      QueryKey.Operation.List,
      query,
    ],
    queryFn: async () => api.internalPortal.vendorsControllerGetVendors(query),
    ...options,
  });

export const useInternalPortalUnassignedToOrganizationVendorsList = (
  organizationId: string,
  query: Parameters<
    typeof api.internalPortal.organizationRelationsControllerGetUnassignedOrganizationVendors
  >[1],
  options?: UseQueryOptions<VendorListDto>
) =>
  useQuery({
    queryKey: [
      QueryKey.Resource.InternalPortalUnassignedToOrganizationVendors,
      QueryKey.Operation.List,
      organizationId,
      query,
    ],
    queryFn: async () =>
      api.internalPortal.organizationRelationsControllerGetUnassignedOrganizationVendors(
        organizationId,
        query
      ),
    ...options,
  });

export const useInternalPortalVendorContactsList = (
  vendorId: string,
  query: NonNullable<
    Parameters<
      typeof api.internalPortal.vendorContactsControllerGetVendorContacts
    >[1]
  >,
  options?: Partial<UseQueryOptions<ContactsListDto>>
) =>
  useQuery({
    ...options,
    enabled: !!vendorId && (options?.enabled ?? true),
    queryKey: [
      QueryKey.Resource.VendorContacts,
      QueryKey.Operation.List,
      vendorId,
      query,
    ],
    queryFn: async () =>
      api.internalPortal.vendorContactsControllerGetVendorContacts(
        vendorId,
        query
      ),
  });

type ApiVendorContactsCsvExportQuery = Parameters<
  typeof api.internalPortal.vendorContactsControllerExportVendorContacts
>[1];

export type VendorContactsCsvExportQuery = Omit<
  ApiVendorContactsCsvExportQuery,
  'columns'
> & {
  columns: string[];
};

export const exportVendorContactsCsv = async (
  vendorId: string,
  query: VendorContactsCsvExportQuery
) =>
  api.internalPortal.vendorContactsControllerExportVendorContacts(
    vendorId,
    query,
    { format: 'blob' }
  ) as Promise<Blob>;

export type UseExportVendorContactsCsvOptions = UseMutationOptions<
  Blob,
  Error,
  VendorContactsCsvExportQuery
> & {
  vendorId: string;
};

export const useExportVendorContactsCsv = ({
  vendorId,
  ...options
}: UseExportVendorContactsCsvOptions) =>
  useMutation({
    ...options,
    mutationFn: variables => exportVendorContactsCsv(vendorId, variables),
  });

export const useInternalPortalVendorContactsInfiniteList = (
  vendorId: string,
  query: NonNullable<
    Parameters<
      typeof api.internalPortal.vendorContactsControllerGetVendorContacts
    >[1]
  >,
  options?: Partial<
    UseInfiniteQueryOptions<
      ContactsListDto,
      Error,
      InfiniteData<ContactsListDto>
    >
  >
) =>
  useInfiniteQuery({
    ...options,
    enabled: !!vendorId && (options?.enabled ?? true),
    queryKey: [
      QueryKey.Resource.VendorContacts,
      QueryKey.Operation.List,
      vendorId,
      query,
      QueryKey.Operation.Infinite,
    ],
    queryFn: async ({ pageParam }) =>
      api.internalPortal.vendorContactsControllerGetVendorContacts(vendorId, {
        ...query,
        page: pageParam as number,
      }),
    getNextPageParam: lastPage =>
      lastPage.hasNextPage ? lastPage.page + 1 : undefined,
    initialPageParam: query.page ?? DEFAULT_PAGE,
    placeholderData: keepPreviousData,
  });

export const useVendorServiceCategories = (
  organizationId: string,
  options?: UseQueryOptions<ServiceCategoryDto[], Error, ServiceCategoryDto[]>
) =>
  useQuery({
    ...options,
    enabled: !!organizationId,
    queryKey: [
      QueryKey.Resource.VendorsServiceCategories,
      QueryKey.Operation.List,
      organizationId,
    ],
    queryFn: async () =>
      api.organizations.organizationVendorsControllerGetAllVendorServiceCategories(
        organizationId
      ),
  });

export const useVendorVectorContracts = (
  organizationId: string,
  vendorId: string,
  query: Parameters<
    typeof api.organizations.vectorContractsControllerGetVectorContracts
  >[2],
  options?: Partial<UseQueryOptions<VectorContractsListDto>>
) =>
  useQuery({
    ...options,
    enabled: !!organizationId && !!vendorId,
    queryKey: [
      QueryKey.Resource.VectorContracts,
      query,
      organizationId,
      vendorId,
    ],
    gcTime: CACHE_TIME,
    staleTime: CACHE_TIME,
    queryFn: async () =>
      api.organizations.vectorContractsControllerGetVectorContracts(
        organizationId,
        vendorId,
        query
      ),
  });

export const useVectorContracts = (
  organizationId: string,
  query: Parameters<
    typeof api.organizations.organizationVectorContractsControllerGetVectorContracts
  >[1],
  options?: Partial<UseQueryOptions<VectorContractsListDto>>
) =>
  useQuery({
    ...options,
    enabled: !!organizationId,
    queryKey: [QueryKey.Resource.VectorContracts, query, organizationId],
    gcTime: CACHE_TIME,
    staleTime: CACHE_TIME,
    queryFn: async () =>
      api.organizations.organizationVectorContractsControllerGetVectorContracts(
        organizationId,
        query
      ),
  });

export const useInternalPortalVendorCreate = (
  options?: Partial<UseMutationOptions<VendorDetailDto, Error, CreateVendorDto>>
) => {
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({
        queryKey: [
          QueryKey.Resource.InternalPortalVendors,
          QueryKey.Operation.List,
        ],
      });
      if (variables.clientIds?.[0])
        queryClient.invalidateQueries({
          queryKey: [
            QueryKey.Resource.InternalPortalVendors,
            QueryKey.Operation.List,
            variables.clientIds?.[0],
          ],
        });

      options?.onSuccess?.(data, variables, context);
    },
    mutationFn: async (data: CreateVendorDto) =>
      api.internalPortal.vendorsControllerCreateVendor(data),
  });
};

type UpdateVendorBillPayPayload = Omit<UpdateVendorBillPayDto, 'file'> & {
  file?: File | null;
};

type VendorBillPayUpdateVariables = {
  vendorId: string;
  isFileChanged?: boolean;
} & UpdateVendorBillPayPayload;

export const useInternalPortalVendorBillPayUpdate = (
  options?: Partial<
    UseMutationOptions<VendorDetailDto, Error, VendorBillPayUpdateVariables>
  >
) => {
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({
        queryKey: [
          QueryKey.Resource.InternalPortalVendors,
          QueryKey.Operation.One,
          variables.vendorId,
        ],
      });
      queryClient.invalidateQueries({
        queryKey: [
          QueryKey.Resource.InternalPortalVendors,
          QueryKey.Operation.List,
        ],
      });
      options?.onSuccess?.(data, variables, context);
    },
    mutationFn: async ({
      vendorId,
      file,
      filesize,
      isFileChanged,
      ...data
    }: VendorBillPayUpdateVariables) => {
      const payload: UpdateVendorBillPayPayload = { ...data };

      if (isFileChanged)
        if (file) {
          payload.filesize = filesize ?? file.size;
          payload.file = file;
        } else payload.file = null;

      return api.internalPortal.vendorsControllerUpdateVendorBillPay(
        vendorId,
        payload as UpdateVendorBillPayDto
      );
    },
  });
};

export const useInternalPortalVendorUpdate = (
  options?: Partial<UseMutationOptions<VendorDetailDto, Error, UpdateVendorDto>>
) => {
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({
        queryKey: [
          QueryKey.Resource.InternalPortalVendors,
          QueryKey.Operation.One,
          variables.vendorId,
        ],
      });
      queryClient.invalidateQueries({
        queryKey: [
          QueryKey.Resource.InternalPortalVendors,
          QueryKey.Operation.List,
        ],
      });
      options?.onSuccess?.(data, variables, context);
    },
    mutationFn: async ({
      vendorId,
      ...data
    }: { vendorId: string } & UpdateVendorDto) =>
      api.internalPortal.vendorsControllerUpdateVendor(vendorId, data),
  });
};

export const useInternalPortalVendorGetById = (
  vendorId?: string,
  options?: Partial<UseQueryOptions<VendorDetailDto>>
) =>
  useQuery({
    ...options,
    enabled: !!vendorId && (options?.enabled ?? true),
    queryKey: [
      QueryKey.Resource.InternalPortalVendors,
      QueryKey.Operation.One,
      vendorId,
    ],
    gcTime: CACHE_TIME,
    staleTime: CACHE_TIME,
    queryFn: async () =>
      api.internalPortal.vendorsControllerGetVendorById(vendorId ?? ''),
  });

export const useInternalPortalVendorAccountsList = (
  organizationId: string,
  query: Parameters<
    typeof api.internalPortal.vendorAccountsControllerGetVendorAccounts
  >[1],
  options?: Partial<UseQueryOptions<VendorAccountListDto>>
) =>
  useQuery({
    ...options,
    enabled: !!organizationId && (options?.enabled ?? true),
    queryKey: [
      QueryKey.Resource.InternalPortalVendorAccounts,
      QueryKey.Operation.List,
      organizationId,
      query,
    ],
    queryFn: async () =>
      api.internalPortal.vendorAccountsControllerGetVendorAccounts(
        organizationId,
        query
      ),
  });

export const useInternalPortalVendorAccountGetById = (
  organizationId?: string,
  accountId?: string,
  options?: Partial<UseQueryOptions<VendorAccountDto>>
) =>
  useQuery({
    ...options,
    enabled: !!organizationId && !!accountId && (options?.enabled ?? true),
    queryKey: [
      QueryKey.Resource.InternalPortalVendorAccounts,
      QueryKey.Operation.One,
      organizationId,
      accountId,
    ],
    gcTime: CACHE_TIME,
    staleTime: CACHE_TIME,
    queryFn: async () =>
      api.internalPortal.vendorAccountsControllerGetVendorAccountById(
        organizationId ?? '',
        accountId ?? ''
      ),
  });

export const useInternalPortalVendorAccountCreate = (
  organizationId: string,
  options?: Partial<
    UseMutationOptions<VendorAccountDto, Error, CreateVendorAccountDto>
  >
) => {
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({
        queryKey: [
          QueryKey.Resource.InternalPortalVendorAccounts,
          QueryKey.Operation.List,
          organizationId,
        ],
      });

      if (variables.auditId)
        invalidateAuditVendorAccountQueries(
          queryClient,
          variables.auditId,
          organizationId
        );

      options?.onSuccess?.(data, variables, context);
    },
    mutationFn: async (data: CreateVendorAccountDto) =>
      api.internalPortal.vendorAccountsControllerCreateVendorAccount(
        organizationId,
        data
      ),
  });
};

export const useInternalPortalVendorAccountCopy = (
  organizationId: string,
  options?: Partial<
    UseMutationOptions<
      VendorAccountDto,
      Error,
      { sourceAccountId: string } & CopyVendorAccountDto
    >
  >
) => {
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({
        queryKey: [
          QueryKey.Resource.InternalPortalVendorAccounts,
          QueryKey.Operation.List,
          organizationId,
        ],
      });

      if (variables.auditId)
        invalidateAuditVendorAccountQueries(
          queryClient,
          variables.auditId,
          organizationId
        );

      options?.onSuccess?.(data, variables, context);
    },
    mutationFn: async ({ sourceAccountId, ...data }) =>
      api.internalPortal.vendorAccountsControllerCopyVendorAccount(
        organizationId,
        sourceAccountId,
        data
      ),
  });
};

export const useInternalPortalVendorAccountFindDuplicates = (
  organizationId: string,
  options?: Partial<
    UseMutationOptions<
      VendorAccountDuplicatesDto,
      Error,
      FindVendorAccountDuplicatesDto
    >
  >
) =>
  useMutation({
    ...options,
    mutationFn: async (data: FindVendorAccountDuplicatesDto) =>
      api.internalPortal.vendorAccountsControllerFindVendorAccountDuplicates(
        organizationId,
        data
      ),
  });

export const useInternalPortalVendorAccountUpdate = (
  organizationId: string,
  options?: Partial<
    UseMutationOptions<
      VendorAccountDto,
      Error,
      { accountId: string } & UpdateVendorAccountDto
    >
  >
) => {
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({
        queryKey: [
          QueryKey.Resource.InternalPortalVendorAccounts,
          QueryKey.Operation.List,
          organizationId,
        ],
      });

      options?.onSuccess?.(data, variables, context);
    },
    mutationFn: async ({ accountId, ...data }) =>
      api.internalPortal.vendorAccountsControllerUpdateVendorAccount(
        organizationId,
        accountId,
        data
      ),
  });
};

export const useInternalPortalVendorSpendCategories = (
  query?: Parameters<
    typeof api.internalPortal.vendorsControllerListSpendCategories
  >[0],
  options?: UseQueryOptions<SpendCategoryListDto>
) =>
  useQuery({
    ...options,
    queryKey: [QueryKey.Resource.InternalPortalVendorSpendCategories, query],
    gcTime: CACHE_TIME,
    staleTime: CACHE_TIME,
    queryFn: async () =>
      api.internalPortal.vendorsControllerListSpendCategories(query),
  });

export const useInternalPortalVendorSpendAreaSelectList = (
  options?: SelectListOptionsQuery
) =>
  useQuery({
    ...options,
    queryKey: [QueryKey.Resource.InternalPortalVendorSpendAreasSelectList],
    gcTime: CACHE_TIME,
    staleTime: CACHE_TIME,
    queryFn: async () =>
      api.internalPortal.vendorsControllerGetSpendAreaSelectList(),
    select: mapSelectListToOptions,
  });

export const useInternalPortalVendorSpendCategorySelectList = (
  options?: SelectListOptionsQuery
) =>
  useQuery({
    ...options,
    queryKey: [QueryKey.Resource.InternalPortalVendorSpendCategoriesSelectList],
    gcTime: CACHE_TIME,
    staleTime: CACHE_TIME,
    queryFn: async () =>
      api.internalPortal.vendorsControllerGetSpendCategorySelectList(),
    select: mapSelectListToOptions,
  });

export const useOrganizationVendorSpendAreaSelectList = (
  organizationId: string,
  options?: SelectListOptionsQuery
) =>
  useQuery({
    ...options,
    enabled: !!organizationId && (options?.enabled ?? true),
    queryKey: [
      QueryKey.Resource.InternalPortalVendorSpendAreasSelectList,
      organizationId,
    ],
    gcTime: CACHE_TIME,
    staleTime: CACHE_TIME,
    queryFn: async () =>
      api.internalPortal.organizationRelationsControllerGetVendorSpendAreaSelectList(
        organizationId
      ),
    select: mapSelectListToOptions,
  });

export const useOrganizationVendorSpendCategorySelectList = (
  organizationId: string,
  options?: SelectListOptionsQuery
) =>
  useQuery({
    ...options,
    enabled: !!organizationId && (options?.enabled ?? true),
    queryKey: [
      QueryKey.Resource.InternalPortalVendorSpendCategoriesSelectList,
      organizationId,
    ],
    gcTime: CACHE_TIME,
    staleTime: CACHE_TIME,
    queryFn: async () =>
      api.internalPortal.organizationRelationsControllerGetVendorSpendCategorySelectList(
        organizationId
      ),
    select: mapSelectListToOptions,
  });

export const useInternalPortalInfiniteSpendCategories = (
  query?: Parameters<
    typeof api.internalPortal.vendorsControllerListSpendCategories
  >[0],
  options?: Partial<
    UseInfiniteQueryOptions<
      SpendCategoryListDto,
      Error,
      InfiniteData<SpendCategoryListDto>
    >
  >
) =>
  useInfiniteQuery({
    ...options,
    queryKey: [QueryKey.Resource.InternalPortalVendorSpendCategories, query],
    queryFn: async ({ pageParam }) =>
      api.internalPortal.vendorsControllerListSpendCategories({
        ...query,
        page: pageParam as number,
      }),
    getNextPageParam: lastPage =>
      lastPage.hasNextPage ? lastPage.page + 1 : undefined,
    initialPageParam: query?.page || DEFAULT_PAGE,
    placeholderData: keepPreviousData,
  });

export const useDownloadVendorsImportTemplate = (
  options?: Partial<UseMutationOptions<Blob, Error, void>>
) =>
  useMutation({
    ...options,
    mutationFn: async () =>
      api.internalPortal.vendorsControllerGetImportTemplate({
        format: 'blob',
      }) as unknown as Blob,
  });

export const useVendorsValidateImport = (
  options?: Partial<
    UseMutationOptions<VendorImportResultDto, Error, FileUploadDto>
  >
) =>
  useMutation({
    ...options,
    mutationFn: async (data: FileUploadDto) =>
      api.internalPortal.vendorsControllerValidateImport(data),
  });

export const useVendorsCreateMany = (
  options?: Partial<UseMutationOptions<void, Error, VendorCreateManyDto>>
) => {
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({
        queryKey: [
          QueryKey.Resource.InternalPortalVendors,
          QueryKey.Operation.List,
        ],
      });

      options?.onSuccess?.(data, variables, context);
    },
    mutationFn: async (data: VendorCreateManyDto) =>
      api.internalPortal.vendorsControllerCreateMany(data),
  });
};

export const useInfiniteInternalPortalVendorOrganizations = (
  vendorId: string,
  query: Parameters<
    typeof api.internalPortal.vendorsControllerGetVendorOrganizations
  >[1],
  options?: Partial<
    UseInfiniteQueryOptions<
      VendorOrganizationSelectListDto,
      Error,
      InfiniteData<VendorOrganizationSelectListDto>
    >
  >
) =>
  useInfiniteQuery({
    ...options,
    queryKey: [
      QueryKey.Resource.InternalPortalVendorOrganizations,
      QueryKey.Operation.List,
      query,
      vendorId,
      QueryKey.Operation.Infinite,
    ],
    queryFn: ({ pageParam }) =>
      api.internalPortal.vendorsControllerGetVendorOrganizations(vendorId, {
        ...query,
        page: pageParam as number,
      }),
    getNextPageParam: lastPage => {
      const { hasNextPage } = lastPage;
      const nextPage = lastPage.page + 1;

      return hasNextPage ? nextPage : undefined;
    },
    initialPageParam: query?.page || DEFAULT_PAGE,
    placeholderData: keepPreviousData,
  });

export const useInternalPortalOrganizationVendors = (
  organizationId: string,
  query: Parameters<
    typeof api.internalPortal.organizationRelationsControllerGetOrganizationVendors
  >[1],
  options?: Partial<UseQueryOptions<VendorClientListDto>>
) =>
  useQuery({
    ...options,
    enabled: !!organizationId && (options?.enabled ?? true),
    queryKey: [
      QueryKey.Resource.InternalPortalVendors,
      QueryKey.Operation.List,
      organizationId,
      query,
    ],
    gcTime: CACHE_TIME,
    staleTime: CACHE_TIME,
    queryFn: async () =>
      api.internalPortal.organizationRelationsControllerGetOrganizationVendors(
        organizationId,
        query
      ),
  });

type ApiOrganizationVendorsCsvExportQuery = Parameters<
  typeof api.internalPortal.organizationRelationsControllerExportOrganizationVendors
>[1];

export type OrganizationVendorsCsvExportQuery = Omit<
  ApiOrganizationVendorsCsvExportQuery,
  'columns'
> & {
  columns: string[];
};

export const exportOrganizationVendorsCsv = async (
  organizationId: string,
  query: OrganizationVendorsCsvExportQuery
) =>
  api.internalPortal.organizationRelationsControllerExportOrganizationVendors(
    organizationId,
    query,
    { format: 'blob' }
  ) as Promise<Blob>;

export type UseExportOrganizationVendorsCsvOptions = UseMutationOptions<
  Blob,
  Error,
  OrganizationVendorsCsvExportQuery
> & {
  organizationId: string;
};

export const useExportOrganizationVendorsCsv = ({
  organizationId,
  ...options
}: UseExportOrganizationVendorsCsvOptions) =>
  useMutation({
    ...options,
    mutationFn: variables =>
      exportOrganizationVendorsCsv(organizationId, variables),
  });

type ApiVendorsCsvExportQuery = Parameters<
  typeof api.internalPortal.vendorsControllerExportVendors
>[0];

export type VendorsCsvExportQuery = Omit<
  ApiVendorsCsvExportQuery,
  'columns'
> & {
  columns: string[];
};

export const exportVendorsCsv = async (query: VendorsCsvExportQuery) =>
  api.internalPortal.vendorsControllerExportVendors(query, {
    format: 'blob',
  }) as Promise<Blob>;

export const useExportVendorsCsv = (
  options?: Partial<UseMutationOptions<Blob, Error, VendorsCsvExportQuery>>
) =>
  useMutation({
    ...options,
    mutationFn: exportVendorsCsv,
  });

export const useInfiniteInternalPortalOrganizationVendors = (
  organizationId: string,
  query: NonNullable<
    Parameters<
      typeof api.internalPortal.organizationRelationsControllerGetOrganizationVendors
    >[1]
  >,
  options?: Partial<
    UseInfiniteQueryOptions<
      VendorClientListDto,
      Error,
      InfiniteData<VendorClientListDto>
    >
  >
) =>
  useInfiniteQuery({
    ...options,
    enabled: !!organizationId && (options?.enabled ?? true),
    queryKey: [
      QueryKey.Resource.InternalPortalVendors,
      QueryKey.Operation.List,
      organizationId,
      query,
      QueryKey.Operation.Infinite,
      ...(Array.isArray(options?.queryKey) ? options.queryKey : []),
    ],
    queryFn: async ({ pageParam }) =>
      api.internalPortal.organizationRelationsControllerGetOrganizationVendors(
        organizationId,
        {
          ...query,
          page: pageParam as number,
        }
      ),
    getNextPageParam: lastPage =>
      lastPage.hasNextPage ? lastPage.page + 1 : undefined,
    initialPageParam: query.page ?? DEFAULT_PAGE,
    placeholderData: keepPreviousData,
  });

interface UseVendorContractsListProps {
  vendorId: string;
  payload: Parameters<
    typeof api.internalPortal.vendorRelationsControllerGetVendorContracts
  >[1];
}

export const useVendorContractsList = (
  { vendorId, payload }: UseVendorContractsListProps,
  options?: Partial<UseQueryOptions<VendorContractListDto>>
) =>
  useQuery({
    ...options,
    enabled: !!vendorId && (options?.enabled ?? true),
    queryKey: [
      QueryKey.Resource.VendorContracts,
      QueryKey.Operation.List,
      vendorId,
      payload,
    ],
    gcTime: CACHE_TIME,
    staleTime: CACHE_TIME,
    queryFn: async () =>
      api.internalPortal.vendorRelationsControllerGetVendorContracts(
        vendorId,
        payload
      ),
  });

type ApiVendorContractsCsvExportQuery = Parameters<
  typeof api.internalPortal.vendorRelationsControllerExportVendorContracts
>[1];

export type VendorContractsCsvExportQuery = Omit<
  ApiVendorContractsCsvExportQuery,
  'columns'
> & {
  columns: string[];
};

export const exportVendorContractsCsv = async (
  vendorId: string,
  query: VendorContractsCsvExportQuery
) =>
  api.internalPortal.vendorRelationsControllerExportVendorContracts(
    vendorId,
    query,
    { format: 'blob' }
  ) as Promise<Blob>;

export type UseExportVendorContractsCsvOptions = UseMutationOptions<
  Blob,
  Error,
  VendorContractsCsvExportQuery
> & {
  vendorId: string;
};

export const useExportVendorContractsCsv = ({
  vendorId,
  ...options
}: UseExportVendorContractsCsvOptions) =>
  useMutation({
    ...options,
    mutationFn: variables => exportVendorContractsCsv(vendorId, variables),
  });
